import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { requireAnyFeature } from '@/common/utils/requireFeature';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  HardDrive,
  Loader2,
  CheckCircle2,
  XCircle,
  Link2,
  Unlink,
  FolderOpen,
  ExternalLink,
} from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  googleDriveIntegrationService,
  type UpdateGoogleDriveConfigData,
} from '@/features/google-drive-integration/services/google-drive-integration.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/google-drive-config')({
  beforeLoad: () =>
    requireAnyFeature(['CONTRACT_MANAGEMENT', 'GOOGLE_WORKSPACE']),
  component: GoogleDriveConfigPage,
});

function GoogleDriveConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [folderTemplates, setFolderTemplates] = useState<string | null>(null);
  const [folderContracts, setFolderContracts] = useState<string | null>(null);
  const [folderContractsSigned, setFolderContractsSigned] = useState<
    string | null
  >(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['google-drive-integration-config'],
    queryFn: () => googleDriveIntegrationService.getConfig(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateGoogleDriveConfigData) =>
      googleDriveIntegrationService.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['google-drive-integration-config'],
      });
      toast({
        title: 'Configuração salva',
        description: 'A integração com o Google Drive/Docs foi atualizada.',
      });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao salvar',
        variant: 'destructive',
      });
    },
  });

  const connectMutation = useMutation({
    mutationFn: () => googleDriveIntegrationService.getOAuthAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao iniciar conexão',
        variant: 'destructive',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => googleDriveIntegrationService.disconnectOAuth(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['google-drive-integration-config'],
      });
      toast({
        title: 'Conta desconectada',
        description: 'A conexão com o Google Drive/Docs foi removida.',
      });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao desconectar',
        variant: 'destructive',
      });
    },
  });

  const handleSaveFolders = () => {
    updateMutation.mutate({
      driveFolderIdTemplates: folderTemplates ?? '',
      driveFolderIdContracts: folderContracts ?? '',
      driveFolderIdContractsSigned: folderContractsSigned ?? '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const currentTemplates =
    folderTemplates ?? config?.driveFolderIdTemplates ?? '';
  const currentContracts =
    folderContracts ?? config?.driveFolderIdContracts ?? '';
  const currentContractsSigned =
    folderContractsSigned ?? config?.driveFolderIdContractsSigned ?? '';

  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Google Drive / Docs (API)</h1>
          <p className="text-muted-foreground mt-2">
            Conecte uma conta Google e configure as pastas do Drive usadas na
            geração de templates e contratos.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Status da integração
            </CardTitle>
            <CardDescription>
              Situação atual da integração com o Google Drive/Docs para este
              tenant.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Disponibilidade:
              </span>
              {config?.available ? (
                <Badge className="gap-1 bg-green-600 hover:bg-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Disponível
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3.5 w-3.5" />
                  Indisponível
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Conta Google conectada:
              </span>
              {config?.oauthConnected ? (
                <Badge className="gap-1 bg-green-600 hover:bg-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Conectada
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  Não conectada
                </Badge>
              )}
            </div>
            {config?.oauthConnected && config?.oauthConnectedEmail && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  E-mail conectado:
                </span>
                <code className="text-sm bg-muted px-2 py-0.5 rounded">
                  {config.oauthConnectedEmail}
                </code>
              </div>
            )}
            {!config?.oauthConfigured && (
              <p className="text-sm text-muted-foreground">
                A conexão via OAuth ainda não está configurada no servidor
                (variáveis GOOGLE_CLIENT_ID/GOOGLE_CLIENT_SECRET). Entre em
                contato com o suporte.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Conectar com Google</CardTitle>
            <CardDescription>
              Conecte uma conta Google para que o sistema possa criar, copiar
              e exportar documentos no Google Drive/Docs em nome dessa conta.
              Ao conectar pela primeira vez, uma pasta "Asseto Integração"
              (com as subpastas Templates, Contratos e Contratos Assinados) é
              criada automaticamente no Drive dessa conta.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.oauthConnected ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                >
                  {disconnectMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Unlink className="h-4 w-4 mr-2" />
                  )}
                  Desconectar
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending || !config?.oauthConfigured}
              >
                {connectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="h-4 w-4 mr-2" />
                )}
                Conectar com Google
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pastas do Google Drive</CardTitle>
            <CardDescription>
              IDs das pastas do Drive (parte final da URL: .../folders/ID)
              usadas para templates, contratos gerados e contratos assinados.
              Deixe em branco para usar a configuração padrão do sistema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.driveFolderIdRoot && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="text-sm">
                  <span className="text-muted-foreground">
                    Pasta criada automaticamente na conexão:{' '}
                  </span>
                  <span className="font-medium">Asseto Integração</span>
                </div>
                <a
                  href={`https://drive.google.com/drive/folders/${config.driveFolderIdRoot}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Abrir no Drive
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
            <div className="space-y-2 max-w-md">
              <Label htmlFor="gdrive-folder-templates">
                Pasta de templates
              </Label>
              <Input
                id="gdrive-folder-templates"
                placeholder="1AbCDeFGhiJKLmnOPQrstUVwxYZ"
                value={currentTemplates}
                onChange={(e) => setFolderTemplates(e.target.value)}
              />
            </div>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="gdrive-folder-contracts">
                Pasta de contratos
              </Label>
              <Input
                id="gdrive-folder-contracts"
                placeholder="1AbCDeFGhiJKLmnOPQrstUVwxYZ"
                value={currentContracts}
                onChange={(e) => setFolderContracts(e.target.value)}
              />
            </div>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="gdrive-folder-contracts-signed">
                Pasta de contratos assinados
              </Label>
              <Input
                id="gdrive-folder-contracts-signed"
                placeholder="1AbCDeFGhiJKLmnOPQrstUVwxYZ"
                value={currentContractsSigned}
                onChange={(e) => setFolderContractsSigned(e.target.value)}
              />
            </div>
            <Button
              onClick={handleSaveFolders}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Salvar pastas
            </Button>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
